import { computed, toValue } from 'vue';
import { useMutation, useQuery } from '@tanstack/vue-query';
import axios from 'axios';

import { queryKeys } from '@/queries/_shared/queryKeys';
import { noCacheHeaders, noCacheQueryOptions } from '@/queries/_shared/noCache';
import http from '@/services/http';

const SDOT_API_BASE = String(import.meta.env.VITE_SDOT_API_URL || '').replace(/\/+$/, '');

function unwrapBody(data) {
  if (data && typeof data === 'object' && 'body' in data) return data.body;
  return data;
}

function readSdotToken() {
  return String(localStorage.getItem('sdotToken') || '');
}

function readOrganizationId() {
  return String(localStorage.getItem('organization_id') || '');
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function parseMaybeJson(value, fallback = null) {
  if (!value || typeof value !== 'string') return value ?? fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function normalizeTaskItem(item) {
  const sdot = item?.sdot_data && typeof item.sdot_data === 'object' ? { ...item.sdot_data } : null;
  if (sdot?.info && typeof sdot.info === 'string') {
    sdot.info = parseMaybeJson(sdot.info, {});
  }
  const source = sdot || {};
  return {
    ...source,
    id: Number(source.id || item?.task_id || 0),
    task_id: Number(item?.id || source.task_id || 0),
    answer: toArray(item?.results),
    end_date: item?.end_date || source.end_date || null,
    status: Number(source.status || 0),
    name: String(source.name || ''),
    time: Number(source.time ?? -1),
    content: source.content || "",
    files: toArray(source.files),
    info: source.info || null,
    pupils: item?.pupils || null,
  };
}

function parsePupils(value) {
  if (!value) return [];
  return String(value)
    .replace(/{|}/g, "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function includesPupil(item, pupilId) {
  const list = parsePupils(item?.pupils);
  if (!list.length) return true;
  return list.includes(String(Number(pupilId || 0)));
}

async function fetchPlanTasks(lessonId) {
  const response = await http.get('/plans/tasks', {
    params: { lplan_id: Number(lessonId || 0) },
    headers: noCacheHeaders,
  });
  return unwrapBody(response.data) || [];
}

async function fetchPlanMaterials(lessonId) {
  const response = await http.get('/plans/materials', {
    params: { lplan_id: Number(lessonId || 0) },
    headers: noCacheHeaders,
  });
  return unwrapBody(response.data) || [];
}

async function fetchPlanTests(lessonId) {
  const response = await http.get('/plans/tests', {
    params: { lplan_id: Number(lessonId || 0) },
    headers: noCacheHeaders,
  });
  return unwrapBody(response.data) || [];
}

async function fetchSdotToken({ serverId, pupilId }) {
  const response = await axios.get(`${SDOT_API_BASE}/school/connect`, {
    params: {
      server_id: Number(serverId || 0),
      pupil_id: Number(pupilId || 0),
    }
  });
  return unwrapBody(response.data) || {};
}

async function fetchSdotTasks(ids, token) {
  if (!ids.length || !token) return [];
  const response = await axios.get(`${SDOT_API_BASE}/school/tasks`, {
    params: { ids: ids.join(",") },
    headers: { ...noCacheHeaders, Authorization: `Bearer ${token}` },
  });
  return unwrapBody(response.data) || [];
}

async function fetchSdotMaterials(ids, token) {
  if (!ids.length || !token) return [];
  const response = await axios.get(`${SDOT_API_BASE}/school/materials`, {
    params: { ids: ids.join(",") },
    headers: { ...noCacheHeaders, Authorization: `Bearer ${token}` },
  });
  return unwrapBody(response.data) || [];
}

async function fetchSdotTests(ids, token) {
  if (!ids.length || !token) return [];
  const response = await axios.get(`${SDOT_API_BASE}/school/tests`, {
    params: { ids: ids.join(",") },
    headers: { ...noCacheHeaders, Authorization: `Bearer ${token}` },
  });
  return unwrapBody(response.data) || [];
}

async function fetchSdotTestResults(testId, token) {
  const response = await axios.get(`${SDOT_API_BASE}/school/test-results`, {
    params: { test_id: Number(testId || 0) },
    headers: { ...noCacheHeaders, Authorization: `Bearer ${token}` },
  });
  return unwrapBody(response.data) || [];
}

export function useSdotTokenMutation(options = {}) {
  return useMutation({
    mutationFn: fetchSdotToken,
    ...options,
  });
}

export function useTaskSdotTokenMutation(options = {}) {
  return useSdotTokenMutation(options);
}

export function useTaskTasksQuery(lessonId, pupilId, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.task.tasks(toValue(lessonId), toValue(pupilId))),
    enabled: computed(
      () => Number(toValue(lessonId) || 0) > 0 && Number(toValue(pupilId) || 0) > 0
    ),
    queryFn: async () => {
      const raw = await fetchPlanTasks(toValue(lessonId));
      const selectedPupilId = Number(toValue(pupilId) || 0);
      const filtered = toArray(raw).filter((item) => includesPupil(item, selectedPupilId));
      const withLocalSdot = filtered.map((item) => normalizeTaskItem(item));

      const missingIds = filtered
        .filter((item) => !(item?.sdot_data && typeof item.sdot_data === 'object'))
        .map((item) => Number(item?.task_id || 0))
        .filter((id) => id > 0);
      if (!missingIds.length) return withLocalSdot;

      const token = readSdotToken();
      if (!token) return withLocalSdot;
      const remote = await fetchSdotTasks(missingIds, token);
      const byId = new Map(
        toArray(remote).map((item) => [Number(item?.id || 0), item])
      );

      return filtered.map((item) => {
        if (item?.sdot_data && typeof item.sdot_data === 'object') return normalizeTaskItem(item);
        const remoteTask = byId.get(Number(item?.task_id || 0));
        if (!remoteTask) return normalizeTaskItem(item);
        return normalizeTaskItem({ ...item, sdot_data: remoteTask });
      });
    },
    ...options,
    ...noCacheQueryOptions,
  });
}

function normalizeMaterialInfo(item) {
  if (!item || typeof item !== 'object') return null;
  const next = { ...item };
  next.type = Number(next.type || 0);
  if (next.info && typeof next.info === 'string') {
    next.info = parseMaybeJson(next.info, {});
  }
  if (next.video && typeof next.video === 'string') {
    next.video = parseMaybeJson(next.video, {});
  }
  if (next.file && typeof next.file === 'string') {
    next.file = parseMaybeJson(next.file, {});
  }
  if (next.type === 6) {
    const info = next.info && typeof next.info === 'object' ? next.info : {};
    next.info = {
      url: info.url || next.url || '',
      is_live: info.is_live ?? next.is_live ?? false,
      live_date: info.live_date || next.live_date || '',
      live_time: info.live_time || next.live_time || '',
      video: info.video || next.video || null,
    };
  }
  return next;
}

export function useTaskMaterialsQuery(lessonId, roleId, pupilId, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.task.materials(toValue(lessonId), toValue(roleId), toValue(pupilId))),
    enabled: computed(
      () => Number(toValue(lessonId) || 0) > 0 && Number(toValue(pupilId) || 0) > 0
    ),
    queryFn: async () => {
      const raw = await fetchPlanMaterials(toValue(lessonId));
      const ids = [];
      const local = [];

      toArray(raw).forEach((item) => {
        const materialId = Number(item?.material_id || 0);
        if (materialId > 0) ids.push(materialId);
        if (item?.sdot_data && typeof item.sdot_data === 'object') {
          const normalized = normalizeMaterialInfo(item.sdot_data);
          if (normalized) local.push(normalized);
        }
      });

      const currentRole = Number(toValue(roleId) || 3);
      if (currentRole === 2) {
        return local.filter((item) => Number(item?.type || 0) !== 5);
      }

      if (ids.length === local.length) return local;
      const token = readSdotToken();
      if (!token) return local;
      const remote = await fetchSdotMaterials(ids, token);
      return toArray(remote)
        .filter((item) => Number(item?.state || 0) !== 0)
        .map((item) => normalizeMaterialInfo(item))
        .filter(Boolean);
    },
    ...options,
    ...noCacheQueryOptions,
  });
}

export function useTaskTestsQuery(lessonId, pupilId, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.task.tests(toValue(lessonId), toValue(pupilId))),
    enabled: computed(
      () => Number(toValue(lessonId) || 0) > 0 && Number(toValue(pupilId) || 0) > 0
    ),
    queryFn: async () => {
      const raw = await fetchPlanTests(toValue(lessonId));
      const ids = toArray(raw)
        .map((item) => Number(item?.test_id || 0))
        .filter((id) => id > 0);
      const token = readSdotToken();
      if (!ids.length || !token) return [];

      const tests = await fetchSdotTests(ids, token);
      const withResults = await Promise.all(
        toArray(tests).map(async (item) => {
          try {
            const results = await fetchSdotTestResults(Number(item?.id || 0), token);
            const sorted = [...toArray(results)].sort(
              (a, b) => Number(a?.finished || 0) - Number(b?.finished || 0)
            );
            const last = sorted[sorted.length - 1];
            return {
              ...item,
              finish: sorted.length > 0,
              correctAnswer: Number(last?.correct_answers || 0),
            };
          } catch {
            return {
              ...item,
              finish: false,
              correctAnswer: 0,
            };
          }
        })
      );
      return withResults;
    },
    ...options,
    ...noCacheQueryOptions,
  });
}

export async function startSdotTestProcess({ hash, lessonId, act = "begin" }) {
  const token = readSdotToken();
  const organizationId = readOrganizationId();
  const response = await axios.post(
    `${SDOT_API_BASE}/school/test-process`,
    {
      act,
      th: hash,
      lesson_plan_id: Number(lessonId || 0),
    },
    {
      headers: {
        ...noCacheHeaders,
        Authorization: `Bearer ${token}`,
        'x-organization-id': organizationId,
      },
    }
  );
  return unwrapBody(response.data) || {};
}

export async function fetchSdotQuestions(testId, expand = 'answers') {
  const token = readSdotToken();
  const organizationId = readOrganizationId();
  const response = await axios.get(`${SDOT_API_BASE}/school/questions`, {
    params: {
      test_id: Number(testId || 0),
      expand,
    },
    headers: {
      ...noCacheHeaders,
      Authorization: `Bearer ${token}`,
      'x-organization-id': organizationId,
    },
  });
  return unwrapBody(response.data) || [];
}

export async function sendSdotTestAnswer({ lessonId, questionId, answers }) {
  const token = readSdotToken();
  const organizationId = readOrganizationId();
  const th = String(localStorage.getItem('th') || '');
  const rh = String(localStorage.getItem('rh') || '');
  if (!th || !rh) throw new Error('Missing test session');

  const response = await axios.post(
    `${SDOT_API_BASE}/school/test-process`,
    {
      act: 'answer',
      th,
      rh,
      lesson_plan_id: Number(lessonId || 0),
      question: {
        id: Number(questionId || 0),
        answers: Array.isArray(answers) ? answers : [],
      },
    },
    {
      headers: {
        ...noCacheHeaders,
        Authorization: `Bearer ${token}`,
        'x-organization-id': organizationId,
      },
    }
  );
  return unwrapBody(response.data) || {};
}

export async function finishSdotTestProcess({ lessonId }) {
  const token = readSdotToken();
  const organizationId = readOrganizationId();
  const th = String(localStorage.getItem('th') || '');
  const rh = String(localStorage.getItem('rh') || '');
  if (!th || !rh) throw new Error('Missing test session');

  const response = await axios.post(
    `${SDOT_API_BASE}/school/test-process`,
    {
      act: 'finish',
      th,
      rh,
      lesson_plan_id: Number(lessonId || 0),
    },
    {
      headers: {
        ...noCacheHeaders,
        Authorization: `Bearer ${token}`,
        'x-organization-id': organizationId,
      },
    }
  );
  return unwrapBody(response.data) || {};
}

export async function joinSdotConference({ conferenceId, organizationId }) {
  const token = readSdotToken();
  const response = await axios.post(
    `${SDOT_API_BASE}/school/conference`,
    {
      act: 'join',
      id: Number(conferenceId || 0),
    },
    {
      headers: {
        ...noCacheHeaders,
        Authorization: `Bearer ${token}`,
        'x-organization-id': String(organizationId || readOrganizationId() || ''),
      },
    }
  );
  return unwrapBody(response.data) || {};
}
